/**
 * PSE-2 — GDELT 2.0 Doc API sentiment fetcher.
 *
 * Endpoint: https://api.gdeltproject.org/api/v2/doc/doc
 *  ?query=<terms>
 *  &mode=ArtList         // article list (not timeline / heatmap)
 *  &format=json
 *  &maxrecords=50        // cap per query — orchestrator caps overall
 *  &sort=hybridrel       // relevance × recency
 *
 * Response shape (subset):
 *  { articles: [ {
 *      url, url_mobile, title, seendate, socialimage,
 *      domain, language, sourcecountry, tone
 *    } ] }
 *
 * GDELT's `tone` is a float in roughly [−10, +10] (the typical signed
 * tone-extractor range; the long tail can stretch wider). PSE-2
 * normalises to [−1, +1] by dividing by 10 and clamping.
 *
 * `btmTech` classification: pattern-match the article body for tech
 * keywords (see `sentimentPolarity.classifyBtmTech`). When the body
 * isn't returned (GDELT's ArtList mode returns title + url metadata
 * only — the body lives behind the `domain` redirect), we classify
 * off the title alone. This is lower-confidence than body classification
 * — `lengthBasedConfidence` handles the dampening.
 *
 * No API key required. A shared queue spaces requests by at least five
 * seconds and respects throttling across both sentiment adapters.
 *
 * NEVER throws. All failure modes collapse into a structured
 * `{ status: 'unavailable', reason, entries: [] }` so the PSE
 * orchestrator stays resilient.
 */
import type {
  PublicSentimentEntry,
} from '../../types/siteEvidence';
import type { PublicSentimentSourceResult } from '../publicSentimentOrchestrator';
import { fetchGdeltDocument } from './gdeltRequest';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';
import {
  classifyBtmTech,
  lengthBasedConfidence,
  truncateSnippet,
} from './sentimentPolarity';

const GDELT_BASE_URL = 'https://api.gdeltproject.org/api/v2/doc/doc';
const DEFAULT_MAX_RECORDS_PER_QUERY = 50;
/**
 * GDELT tone range — the v2 GKG / Doc API tone-extractor emits values
 * roughly in [−10, +10]. Long tail samples exist past this band; the
 * normalization clamps so a single outlier doesn't blow out the
 * downstream confidence-weighted average.
 */
const GDELT_TONE_RANGE = 10;

/* ── Public types ──────────────────────────────────────────────────── */

export interface GdeltSentimentFilters {
  /** Attempt-owned report cancellation/watchdog signal. */
  signal?: AbortSignal;
  /** Soft cap per query. Default 50; GDELT enforces a hard 250-max. */
  maxRecordsPerQuery?: number;
  trendWindowDays?: number;
  /** Sort order. Default 'hybridrel' (relevance × recency). */
  sort?: 'hybridrel' | 'datedesc' | 'dateasc' | 'tonedesc' | 'toneasc';
}

interface GdeltArticle {
  url?: string;
  url_mobile?: string;
  title?: string;
  seendate?: string;
  socialimage?: string;
  domain?: string;
  language?: string;
  sourcecountry?: string;
  tone?: number | string;
}

interface GdeltResponse {
  articles?: GdeltArticle[];
}

/* ── Single-query fetcher ──────────────────────────────────────────── */

/**
 * Map a single GDELT article record to a `PublicSentimentEntry`. Returns
 * null when required fields (url, title) are missing — these entries
 * can't be deduped (no URL key) or rendered (no title for the snippet),
 * so dropping them is safer than emitting half-populated entries.
 */
export function parseGdeltSourceDate(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.replace(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/, '$1-$2-$3T$4:$5:$6Z');
  const date = Date.parse(normalized);
  return Number.isFinite(date) ? new Date(date).toISOString() : undefined;
}

export function mapGdeltArticle(article: GdeltArticle): PublicSentimentEntry | null {
  const url = article.url?.trim() || article.url_mobile?.trim();
  const title = article.title?.trim();
  if (!url || !title) return null;

  // GDELT's `tone` arrives as either number or string-encoded number.
  const toneRaw = typeof article.tone === 'number'
    ? article.tone
    : typeof article.tone === 'string'
      ? Number(article.tone.trim() || NaN)
      : NaN;
  const tone = Number.isFinite(toneRaw) ? toneRaw : 0;

  // Normalise tone to [−1, +1] using the GDELT tone-range envelope.
  const sentimentScore = Math.max(-1, Math.min(1, tone / GDELT_TONE_RANGE));

  // The title alone is the classification surface for ArtList mode —
  // GDELT doesn't return the article body inline. The classifier
  // falls back to 'general-datacenter' when no specific tech matches.
  const btmTech = classifyBtmTech(title);

  // Length-based confidence using the title length as a proxy. A
  // 140-char headline yields conf ≈ 0.07; a longer GDELT title (some
  // sources expose summaries up to 500 chars) approaches 0.25. The
  // orchestrator's confidence-weighted aggregator dampens these
  // appropriately against higher-confidence federal-docket entries.
  const confidence = Number.isFinite(toneRaw) ? lengthBasedConfidence(title) : 0;

  const fetchedAt = new Date().toISOString();

  return {
    sourceId: 'gdelt',
    fetchedAt,
    ...(parseGdeltSourceDate(article.seendate) ? { observedDate: parseGdeltSourceDate(article.seendate), dateBasis: 'first-seen' as const } : {}),
    btmTech,
    sentimentScore,
    confidence,
    supportingSnippet: truncateSnippet(title),
    url,
  };
}

/**
 * Issue one GDELT query and return mapped entries. Side-effect free
 * apart from the HTTP call — cross-query dedup happens in the
 * top-level `fetchGdeltSentiment`.
 */
async function fetchOneQuery(
  query: string,
  filters: GdeltSentimentFilters,
): Promise<PublicSentimentEntry[]> {
  const maxRecords = filters.maxRecordsPerQuery ?? DEFAULT_MAX_RECORDS_PER_QUERY;
  const sort = filters.sort ?? 'hybridrel';

  const params = new URLSearchParams({
    query,
    mode: 'ArtList',
    format: 'json',
    maxrecords: String(maxRecords),
    sort,
    timespan: `${Math.max(1, Math.min(1825, filters.trendWindowDays ?? 365))}d`,
  });

  const url = `${GDELT_BASE_URL}?${params.toString()}`;
  const res = await fetchGdeltDocument(url, 20_000, filters.signal);
  if (!res.ok) {
    throw new Error(`gdelt: HTTP ${res.status}`);
  }
  // GDELT returns an empty body (NOT JSON) when no results match.
  const text = await res.text();
  if (!text.trim()) return [];
  let parsed: GdeltResponse;
  try {
    parsed = JSON.parse(text) as GdeltResponse;
  } catch {
    throw new Error('gdelt: non-JSON response (throttle or upstream failure)');
  }
  if (!Array.isArray(parsed.articles)) throw new Error('gdelt: response did not contain an articles array');
  const articles = parsed.articles;

  const out: PublicSentimentEntry[] = [];
  for (const a of articles) {
    const mapped = mapGdeltArticle(a);
    if (mapped) out.push(mapped);
  }
  return out;
}

/* ── Public entry point ────────────────────────────────────────────── */

/**
 * PSE-2 fetcher — structurally satisfies `PublicSentimentSourceFn`.
 *
 * Issues queries through the shared five-second GDELT request queue. The orchestrator
 * also caches per `(sourceId, tech)` so two BTM techs that share queries
 * don't double-fetch.
 *
 * Resilience model:
 *  - Per-query try/catch — one failing query doesn't kill the batch.
 *  - Empty articles array → return `{ status: 'ok', entries: [] }`.
 *  - All queries failed → return `{ status: 'unavailable', reason }`.
 *
 * NEVER throws.
 */
export async function fetchGdeltSentiment(
  queries: string[],
  filters: GdeltSentimentFilters = {},
): Promise<PublicSentimentSourceResult> {
  throwIfAborted(filters.signal);
  if (queries.length === 0) {
    return { status: 'unavailable', reason: 'no queries supplied', entries: [] };
  }

  const allEntries: PublicSentimentEntry[] = [];
  const errors: string[] = [];
  for (const q of queries) {
    throwIfAborted(filters.signal);
    try {
      const entries = await fetchOneQuery(q, filters);
      throwIfAborted(filters.signal);
      allEntries.push(...entries);
    } catch (e) {
      rethrowIfAborted(filters.signal, e);
      errors.push(`${q}: ${(e as Error).message ?? String(e)}`);
    }
  }

  // No entries AND every query errored → unavailable. No entries with
  // some queries succeeding (just returning [] articles) → ok with
  // empty entries; the orchestrator handles the "no signal" case.
  if (allEntries.length === 0 && errors.length === queries.length) {
    return {
      status: 'unavailable',
      reason: errors.join('; '),
      entries: [],
    };
  }

  // Cross-query URL dedup — the same article often surfaces against
  // multiple seed queries (broad + locality-suffixed). First-seen wins.
  const seen = new Set<string>();
  const deduped: PublicSentimentEntry[] = [];
  for (const e of allEntries) {
    if (seen.has(e.url)) continue;
    seen.add(e.url);
    deduped.push(e);
  }

  return { status: 'ok', entries: deduped };
}
